import { useState } from "react";
import { Baby, Sparkles, Loader2, Hash, PawPrint, Heart, Music2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { QUALITIES } from "./GeneratorForm";
import { YoutubePublishOptions } from "./YoutubePublishOptions";

const RHYME_STYLES = [
  { key: "counting", label: "Counting Rhyme", icon: Hash, testid: "rhyme-style-counting" },
  { key: "animal", label: "Animal Rhyme", icon: PawPrint, testid: "rhyme-style-animal" },
  { key: "moral", label: "Moral Story Rhyme", icon: Heart, testid: "rhyme-style-moral" },
  { key: "classic", label: "Classic Nursery", icon: Music2, testid: "rhyme-style-classic" },
];

const PRESETS = [
  { key: "counting-friends", label: "Counting Fun with Friends", testid: "rhyme-preset-counting" },
  { key: "farm-animals", label: "Farm Animal Sounds", testid: "rhyme-preset-animal" },
  { key: "sharing", label: "Sharing is Caring", testid: "rhyme-preset-moral" },
];

export const RhymeGeneratorForm = ({ onCreated }) => {
  const [rhymeStyle, setRhymeStyle] = useState("counting");
  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState("hi");
  const [quality, setQuality] = useState("720p");
  const [submitting, setSubmitting] = useState(false);
  const [publishOpts, setPublishOpts] = useState({
    auto_publish: false,
    publish_privacy: "private",
    publish_at_landscape: "",
    publish_at_shorts: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      toast.error("Please enter a theme for the rhyme");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/projects", {
        topic: topic.trim(),
        kind: "song",
        song_type: "rhyme",
        rhyme_style: rhymeStyle,
        language,
        quality,
        auto_publish: publishOpts.auto_publish,
        publish_privacy: publishOpts.publish_privacy,
        publish_at_landscape:
          publishOpts.publish_privacy === "schedule" && publishOpts.publish_at_landscape
            ? new Date(publishOpts.publish_at_landscape).toISOString()
            : null,
        publish_at_shorts:
          publishOpts.publish_privacy === "schedule" && publishOpts.publish_at_shorts
            ? new Date(publishOpts.publish_at_shorts).toISOString()
            : null,
      });
      onCreated(res.data.id);
    } catch (e) {
      toast.error("Failed to start rhyme generation");
      setSubmitting(false);
    }
  };

  return (
    <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-teal-400/40 via-slate-800 to-slate-800">
      <div
        className="rounded-2xl bg-slate-900/80 border border-slate-800/80 p-6 sm:p-8 backdrop-blur-md"
        style={{ backgroundImage: "radial-gradient(circle at 50% -20%, rgba(45, 212, 191, 0.14), transparent 70%)" }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Baby className="w-6 h-6 text-teal-400" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">
            Kids Rhymes &amp; Cartoons
          </h2>
        </div>
        <p className="text-sm text-slate-400 mb-6">
          AI writes super simple, joyful rhyme lyrics, Suno composes bright bouncy music that starts
          energetic from second one, and we turn it into <span className="text-teal-300">4 videos</span> —
          2 landscape + 2 shorts. Rendered one at a time.
        </p>

        <div className="grid grid-cols-2 gap-2 mb-5">
          {RHYME_STYLES.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                type="button"
                data-testid={s.testid}
                onClick={() => setRhymeStyle(s.key)}
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm border transition-all ${
                  rhymeStyle === s.key
                    ? "bg-teal-400 text-black border-teal-400"
                    : "bg-slate-950/80 border-slate-800 text-slate-300"
                }`}
              >
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={p.testid}
              onClick={() => setTopic(p.label)}
              className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/60 px-3 py-1.5 rounded-full text-xs transition-all"
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5">
          <input
            data-testid="rhyme-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. counting to 10, jungle animals, sharing toys..."
            className="w-full bg-slate-950/80 border border-slate-800 focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 text-slate-100 placeholder-slate-500 rounded-xl p-4 text-base sm:text-lg transition-all outline-none"
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Language</label>
              <select
                data-testid="rhyme-language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-teal-400 outline-none"
              >
                <option value="hi">Hindi</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Quality</label>
              <div className="flex gap-1.5">
                {QUALITIES.map((q) => (
                  <button
                    key={q.key}
                    type="button"
                    data-testid={`rhyme-${q.testid}`}
                    onClick={() => setQuality(q.key)}
                    title={q.hint}
                    className={`flex-1 rounded-xl px-2 py-2.5 text-sm border transition-all ${
                      quality === q.key
                        ? "bg-teal-400 text-black border-teal-400"
                        : "bg-slate-950/80 border-slate-800 text-slate-300"
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Both <span className="text-slate-300">landscape</span> and <span className="text-slate-300">shorts</span> are
            generated automatically. Shorts are automatically trimmed to start right when the singing
            begins and capped under 3 minutes.
          </p>

          <YoutubePublishOptions kind="song" slot="rhyme" value={publishOpts} onChange={setPublishOpts} testidPrefix="rhyme-publish" />

          <button
            data-testid="generate-rhyme-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-teal-400 text-black hover:bg-teal-300 font-semibold px-6 py-4 rounded-xl shadow-lg shadow-teal-400/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 text-base"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {submitting ? "Starting composition..." : "Generate 4 Rhyme Videos"}
          </button>
          <p className="text-xs text-slate-500 text-center">Music composition takes ~1-2 min; the 4 videos then render one at a time.</p>
        </form>
      </div>
    </div>
  );
};
